import { computed, onScopeDispose, ref, watch, type Ref } from 'vue'
import { useRoute, useRouter, type LocationQuery, type LocationQueryValue } from 'vue-router'
import { useStorage } from '@vueuse/core'
import { debounce } from 'lodash'

interface UseFilterStateOptions {
    /**
     * Sorting applied when the URL carries no `sort` parameter. Screens without a
     * sort dropdown can leave it out, and `sort` is then never written to the URL.
     */
    defaultSort?: string
    /**
     * The screen's currently rendered rows, used to tell "this account has nothing"
     * apart from "the active filters match nothing".
     */
    items?: Ref<Array<unknown>>
}

type QueryValue = LocationQueryValue | LocationQueryValue[] | undefined

const SEARCH_SYNC_DELAY = 500

const OWNED_KEYS: ReadonlyArray<string> = ['categories', 'brands', 'models', 'grades', 'storage', 'search', 'sort']

/**
 * The filters every screen was last left with, by route path, so that a screen
 * reached without an explicit query (a sidebar link, the prices redirect) comes
 * back as it was. Per tab: a new tab starts clean.
 */
const rememberedFilters = useStorage<Record<string, LocationQuery>>('filters', {}, sessionStorage)

export const forgetRememberedFilters = (): void => {
    rememberedFilters.value = {}
}

const ownedPart = (query: LocationQuery): LocationQuery => {
    return Object.fromEntries(Object.entries(query).filter(([key]) => OWNED_KEYS.includes(key)))
}

const foreignPart = (query: LocationQuery): LocationQuery => {
    return Object.fromEntries(Object.entries(query).filter(([key]) => !OWNED_KEYS.includes(key)))
}

const toStringArray = (value: QueryValue): Array<string> => {
    if (value === undefined || value === null) {
        return []
    }

    if (Array.isArray(value)) {
        return value.filter((entry): entry is string => entry !== null)
    }

    return [value]
}

const toStringValue = (value: QueryValue): string => {
    return toStringArray(value).at(0) ?? ''
}

/**
 * Filter state for the Catalog, Prices and Competitors screens, mirrored into the
 * route query so it survives navigating to a detail page and back, a page refresh
 * and a shared link. Pagination is deliberately left out: every filter change
 * restarts from the first page.
 */
export function useFilterState(options: UseFilterStateOptions = {}) {
    const { defaultSort = '', items } = options

    const route = useRoute()
    const router = useRouter()

    const activeCategoriesFilter: Ref<Array<string>> = ref([])
    const activeBrandsFilters: Ref<Array<string>> = ref([])
    const activeModelsFilters: Ref<Array<string>> = ref([])
    const activeGradesFilters: Ref<Array<string>> = ref([])
    const activeStorageFilters: Ref<Array<string>> = ref([])
    const activeSearchFilter: Ref<string> = ref('')
    const selectedCriteria: Ref<string> = ref(defaultSort)

    const filtersCount = computed(
        () =>
            activeCategoriesFilter.value.length +
            activeBrandsFilters.value.length +
            activeModelsFilters.value.length +
            activeGradesFilters.value.length +
            activeStorageFilters.value.length,
    )

    const hasActiveFilters = computed(() => filtersCount.value > 0 || activeSearchFilter.value !== '')

    // Keep the toolbar reachable when a filter matches nothing, otherwise the user
    // cannot clear the filter that emptied the list
    const hasData = computed(() => (items?.value.length ?? 0) > 0 || hasActiveFilters.value)

    const buildQuery = (): LocationQuery => ({
        ...foreignPart(route.query),
        ...(activeCategoriesFilter.value.length && { categories: activeCategoriesFilter.value }),
        ...(activeBrandsFilters.value.length && { brands: activeBrandsFilters.value }),
        ...(activeModelsFilters.value.length && { models: activeModelsFilters.value }),
        ...(activeGradesFilters.value.length && { grades: activeGradesFilters.value }),
        ...(activeStorageFilters.value.length && { storage: activeStorageFilters.value }),
        ...(activeSearchFilter.value && { search: activeSearchFilter.value }),
        ...(selectedCriteria.value !== defaultSort && { sort: selectedCriteria.value }),
    })

    const remember = () => {
        rememberedFilters.value[route.path] = ownedPart(buildQuery())
    }

    const isUrlInSync = () => router.resolve({ query: buildQuery(), hash: route.hash }).fullPath === route.fullPath

    const syncUrl = () => {
        remember()

        if (isUrlInSync()) {
            return
        }

        router.replace({ query: buildQuery(), hash: route.hash })
    }

    // An explicit query wins; without one, the screen comes back as it was left
    const hydrate = () => {
        const owned = ownedPart(route.query)
        const source = Object.keys(owned).length > 0 ? owned : (rememberedFilters.value[route.path] ?? {})

        activeCategoriesFilter.value = toStringArray(source.categories)
        activeBrandsFilters.value = toStringArray(source.brands)
        activeModelsFilters.value = toStringArray(source.models)
        activeGradesFilters.value = toStringArray(source.grades)
        activeStorageFilters.value = toStringArray(source.storage)
        activeSearchFilter.value = toStringValue(source.search)
        selectedCriteria.value = toStringValue(source.sort) || defaultSort
    }

    hydrate()
    syncUrl()

    watch(
        [
            activeCategoriesFilter,
            activeBrandsFilters,
            activeModelsFilters,
            activeGradesFilters,
            activeStorageFilters,
            selectedCriteria,
        ],
        syncUrl,
    )

    // The search box mutates on every keystroke, and browsers rate-limit history
    // writes, so let the URL catch up at the same pace as the request it triggers
    const syncUrlAfterTyping = debounce(syncUrl, SEARCH_SYNC_DELAY)

    watch(activeSearchFilter, syncUrlAfterTyping)

    onScopeDispose(() => syncUrlAfterTyping.cancel())

    // Screens with a route param keep their component instance when only the param
    // changes, so the URL can move under a live instance (a sidebar link, back/forward,
    // a hand-edited hash) without setup running again. Mirror it synchronously, so a
    // watcher on that param reacting to the same navigation already sees the adopted
    // filters. This composable only ever rewrites the query of the path it is on, so a
    // same-path change that already matches the filters is its own and needs no adopting.
    watch(
        [() => route.path, () => route.query],
        ([path], [previousPath]) => {
            if (path === previousPath && isUrlInSync()) {
                return
            }

            syncUrlAfterTyping.cancel()
            hydrate()
        },
        { flush: 'sync' },
    )

    const resetFilters = () => {
        activeCategoriesFilter.value = []
        activeBrandsFilters.value = []
        activeModelsFilters.value = []
        activeGradesFilters.value = []
        activeStorageFilters.value = []
        activeSearchFilter.value = ''

        // Clearing the search box must not wait out the typing delay
        syncUrlAfterTyping.cancel()
        syncUrl()
    }

    return {
        activeCategoriesFilter,
        activeBrandsFilters,
        activeModelsFilters,
        activeGradesFilters,
        activeStorageFilters,
        activeSearchFilter,
        selectedCriteria,
        filtersCount,
        hasActiveFilters,
        hasData,
        resetFilters,
    }
}
